import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ChevronDown } from "lucide-react";
import { COUPLE } from "@/data/wedding";
import { GoldDivider } from "./Ornaments";
import { WEDDING } from "@/constants/testIds";

const letter = {
  hidden: { y: "110%" },
  show: (i) => ({
    y: "0%",
    transition: { duration: 1, ease: [0.22, 1, 0.36, 1], delay: 0.4 + i * 0.08 },
  }),
};

const MaskedWord = ({ text, className }) => (
  <span className={`inline-flex overflow-hidden ${className}`}>
    {text.split("").map((c, i) => (
      <motion.span key={i} custom={i} variants={letter} className="inline-block">
        {c === " " ? "\u00A0" : c}
      </motion.span>
    ))}
  </span>
);

export const Hero = ({ onNavigate }) => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });

  const brideY = useTransform(scrollYProgress, [0, 1], [0, -160]);
  const groomY = useTransform(scrollYProgress, [0, 1], [0, 140]);
  const brideX = useTransform(scrollYProgress, [0, 1], [0, -60]);
  const groomX = useTransform(scrollYProgress, [0, 1], [0, 60]);
  const textY = useTransform(scrollYProgress, [0, 1], [0, 120]);
  const textOpacity = useTransform(scrollYProgress, [0, 0.6], [1, 0]);

  return (
    <section
      ref={ref}
      className="relative flex h-[100svh] min-h-[640px] w-full items-center justify-center overflow-hidden"
    >
      {/* Radiant kasavu backdrop */}
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,#ffffff_0%,#fdfbf7_45%,#f4ead2_100%)]" />

      {/* Bride — left */}
      <picture>
        <source srcSet={COUPLE.bride.imgWebp} type="image/webp" />
        <motion.img
          src={COUPLE.bride.img}
          alt="Bride"
          style={{ y: brideY, x: brideX }}
          initial={{ opacity: 0, filter: "blur(12px)" }}
          animate={{ opacity: 1, filter: "blur(0px)" }}
          transition={{ duration: 1.4, ease: "easeOut" }}
          className="absolute bottom-0 left-0 z-10 h-[62vh] max-h-[720px] w-auto object-contain drop-shadow-[0_30px_40px_rgba(128,0,0,0.15)] md:h-[86vh] lg:left-[2%]"
          draggable={false}
        />
      </picture>

      {/* Groom — right */}
      <picture>
        <source srcSet={COUPLE.groom.imgWebp} type="image/webp" />
        <motion.img
          src={COUPLE.groom.img}
          alt="Groom"
          style={{ y: groomY, x: groomX }}
          initial={{ opacity: 0, filter: "blur(12px)" }}
          animate={{ opacity: 1, filter: "blur(0px)" }}
          transition={{ duration: 1.4, ease: "easeOut", delay: 0.15 }}
          className="absolute bottom-0 right-0 z-10 h-[62vh] max-h-[720px] w-auto object-contain drop-shadow-[0_30px_40px_rgba(128,0,0,0.15)] md:h-[86vh] lg:right-[2%]"
          draggable={false}
        />
      </picture>

      {/* Center content */}
      <motion.div
        style={{ y: textY, opacity: textOpacity }}
        className="relative z-20 flex flex-col items-center px-6 pb-28 text-center md:pb-44"
      >
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, delay: 0.1 }}
          className="font-malayalam text-2xl font-semibold text-[var(--maroon)] md:text-3xl"
        >
          {COUPLE.title}
        </motion.p>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, delay: 0.2 }}
          className="mt-4 font-body text-[11px] uppercase tracking-mega text-[var(--gold-deep)] md:text-xs"
        >
          Together with their families
        </motion.p>

        <div className="mt-6 flex flex-col items-center leading-none text-[var(--ink)]">
          <MaskedWord
            text={COUPLE.bride.name}
            className="font-display text-5xl italic sm:text-6xl md:text-7xl"
          />
          <motion.span
            initial={{ opacity: 0, scale: 0.6 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8, delay: 1.1 }}
            className="font-script my-1 text-5xl text-[var(--gold)] md:text-7xl"
          >
            &amp;
          </motion.span>
          <MaskedWord
            text={COUPLE.groom.name}
            className="font-display text-5xl italic sm:text-6xl md:text-7xl"
          />
        </div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1, delay: 1.4 }}
          className="mt-8 flex flex-col items-center"
        >
          <GoldDivider />
          <p className="mt-5 font-display text-xl tracking-wide text-[var(--muted)] md:text-2xl">
            {COUPLE.savingDate}
          </p>
          <p className="mt-1 font-body text-[11px] uppercase tracking-[0.3em] text-[var(--green-deep)]">
            {COUPLE.hashtag}
          </p>
        </motion.div>
      </motion.div>

      {/* Scroll cue */}
      <motion.button
        data-testid={WEDDING.heroScrollCue}
        onClick={() => onNavigate("invitation")}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 1, delay: 2 }}
        className="absolute bottom-7 left-1/2 z-30 flex -translate-x-1/2 flex-col items-center gap-2 text-[var(--maroon)]"
      >
        <span className="font-body text-[10px] uppercase tracking-[0.3em]">Scroll</span>
        <motion.span
          animate={{ y: [0, 8, 0] }}
          transition={{ duration: 1.6, repeat: Infinity, ease: "easeInOut" }}
        >
          <ChevronDown className="h-5 w-5" strokeWidth={1.4} />
        </motion.span>
      </motion.button>
    </section>
  );
};
